'use client';

import Accordion from '@dvd/components/Accordion';
import BaseText from '@dvd/components/BaseText';
import Section from '@dvd/components/Section';
import TestimonialCard from '@dvd/components/TestimonialCard';
import TextLink from '@dvd/components/TextLink';
import Cta from '@dvd/modules/Cta';
import FAQ from '@dvd/modules/FAQ';
import FeatureList from '@dvd/modules/FeatureList';
import FullColumnText from '@dvd/modules/FullColumnText';
import GiantVideo from '@dvd/modules/GiantVideo';
import G2SocialProof from '@dvd/modules/G2SocialProof';
import Hero from '@dvd/modules/Hero';
import Scrolling, { ScrollingChild } from '@dvd/modules/Scrolling';
import SplitText from '@dvd/modules/Splits/SplitText';

import { WISTIA_FREE_ACCOUNT } from '@lib/constants';
import { useClientExperiment } from '@hooks/useExperiment';
import {
  ExperimentNames,
  ExperimentRoutes,
  getExperiment,
} from '@config/experiments';

const experiment = getExperiment({
  route: ExperimentRoutes.ProductHosting,
  name: ExperimentNames.FreeTrialHosting,
});

const Page = () => {
  const cohort = useClientExperiment(experiment);
  let ctaText = '';
  switch (cohort) {
    case 'variant_a':
      ctaText = 'Start a free trial';
      break;
    case 'variant_b':
      ctaText = 'Try it for free';
      break;
    case 'control':
    default:
      ctaText = 'See our plans';
      break;
  }

  return (
    <>
      <Section colorScheme="purple">
        <Hero
          description="Elevate your videos with top-notch video playback, a super customizable player, detailed video analytics, and powerful tools for organizing and sharing."
          hashedId="fmyo73k83i"
          headline="High performance hosting"
          primaryCtaHref="/pricing"
          primaryCtaText={ctaText}
          title="Video hosting"
        />
      </Section>

      <Section colorScheme="blue">
        <GiantVideo
          hashedId="cxxg3mcicx"
          headline="No ads, no distractions"
          description="Give your audience a seamless viewing experience."
        >
          <BaseText
            body="Videos hosted with Wistia are 100% ad-free, keeping your viewers’ attention on what matters most: your content."
            heading="Completely ad-free"
            headingLevel="h3"
          />
          <BaseText
            body="Give your viewers content that plays perfectly every time with global delivery, adaptive streaming, a responsive player, and 4K support."
            heading="Quality playback"
            headingLevel="h3"
          />
          <BaseText
            body="The Wistia player is super durable and lightweight, meaning embedded videos load up at the blink of an eye. That’s better for SEO and better for your viewers."
            heading="Super fast load times"
            headingLevel="h3"
          />
        </GiantVideo>
      </Section>

      <Section colorScheme="light">
        <Scrolling
          description="Customize your videos to keep your viewers tuned in."
          headline="Complete control over your videos"
        >
          <ScrollingChild
            cta={{
              href: '/product/player',
              text: 'Learn more about our player',
              trackingLabel: 'player',
              trackingProperty: 'product-hosting',
            }}
            description="No one knows your audience like you do. That’s why we give you complete control of their viewing experience. Change your player's logo, thumbnail, color, and shape—all in seconds."
            headline="Customizable player"
            imageAlt="Video software UI showing a color picker on an image of a smiling woman"
            imageSrc="https://images.ctfassets.net/buksmp4dkugj/1pxv8Tay2TRcV85oAjCdhD/c2293e63b91322bf1d1df1dc7aaf4c1d/hosting-customize.jpg"
          />
          <ScrollingChild
            cta={{
              href: '/product/channels',
              text: 'Check out video collections',
              trackingLabel: 'collections',
              trackingProperty: 'product-hosting',
            }}
            description="Make your video collections easier to find and more fun to watch with SEO-friendly playlists, channels, and carousels that look perfectly on-brand."
            headline="Video collections"
            imageAlt="Video software UI showing three videos on a webpage"
            imageSrc="https://images.ctfassets.net/buksmp4dkugj/17CjpVihnLCJPAT3AVFwj3/04ce346a0483e9caae0ffa901420d0a5/Hosting-Galleries.jpg"
          />
          <ScrollingChild
            cta={{
              href: '/product/embeds',
              text: 'Learn more about embedding',
              trackingLabel: 'embedding',
              trackingProperty: 'product-hosting',
            }}
            description="We’ll automatically generate the embed code. All you have to do is copy and paste it onto your web page for a super fast and responsive video exactly where you want it."
            headline="Easy embedding"
            imageAlt="Video software UI showing an embed code over a video player"
            imageSrc="https://images.ctfassets.net/buksmp4dkugj/3cY3hW6G2jMGompS8wJIPA/b84f7452dd670b722b89533673f0d466/Hosting-Embed.jpg"
          />
          <ScrollingChild
            cta={{
              href: '/product/localize',
              text: 'Localize your videos',
              trackingLabel: 'localize',
              trackingProperty: 'product-hosting',
            }}
            description="Reach a global audience with cost-effective AI-powered translations, captions, and next generation Video Dubbing with lip-sync."
            headline="Localization"
            imageAlt="A video player shows a smiling person in a yellow hoodie. A dropdown menu is open, selecting “Spanish,” highlighting Wistia’s AI-powered Video Dubbing feature."
            imageSrc="https://images.ctfassets.net/buksmp4dkugj/1yEUY7tr2Y4xQ8vWqnBwNi/86608b6e2fce907d1a0d3b93ed4785dd/Hosting_Scroll_Image_4.jpg"
          />
        </Scrolling>
        <Cta
          headline="Like what you see?"
          primaryCtaHref={WISTIA_FREE_ACCOUNT}
          primaryCtaText="Sign up for free"
        />
        <FullColumnText bottomPadding={false} topPadding={false}>
          <h2>Easy-breezy content management</h2>
          <p>
            Toss all your videos into Wistia and we’ll take care of the rest.
          </p>
          <TextLink
            cta={{
              href: '/product/video-management',
              text: 'Learn more about video management',
            }}
          />
        </FullColumnText>
        <SplitText
          description="Getting all your videos into Wistia is a breeze. Just drag and drop files from your hard drive or connect Wistia to Zoom or Descript to import your videos from those platforms."
          hashedId="moqmjvxxet"
          headline="Import"
          mediaOrientation="start"
        />
        <SplitText
          description="We’ve got what you need to keep your video library in tip-top shape. Create folders for your content, add custom tags so you can find your videos faster, and archive old videos without losing their performance metrics."
          hashedId="g2pfyce5nn"
          headline="Organize"
          mediaOrientation="end"
        />
        <SplitText
          description="Find what you’re looking for a whole lot faster with our type-ahead search. We pull from your video titles and the content itself to deliver the best results. Dig in further with filters like tags, duration, creation date, and more."
          hashedId="x4kw2dgxlv"
          headline="Search"
          mediaOrientation="start"
        />
        <SplitText
          description="Whether you have a single video or an entire folder’s worth of content, password protection lets you control who sees what. And with customizable user permissions, you decide who can access and edit your video library."
          hashedId="k674e79vz2"
          headline="Secure"
          mediaOrientation="end"
        />
        <SplitText
          ctaLink={{
            href: '/product/analytics',
            text: 'Learn more about analytics',
          }}
          description="Track performance, optimize your videos, and drive real results. Plus, integrate Wistia with marketing tools like Google Analytics, HubSpot, and Pardot to supercharge your overall marketing strategy."
          headline="Analytics built for action"
          imageAlt="Video software UI showing a total plays counter over a video with an engagement graph overlay"
          imageHeight={583}
          imageSrc="https://images.ctfassets.net/buksmp4dkugj/4PhMFqAJQxO6N7jgAo29e8/5befb4314511d77e83fe6539b3be3ba6/hosting-analyics.jpg"
          imageWidth={528}
          mediaOrientation="start"
        />
      </Section>

      <Section colorScheme="blueLight">
        <G2SocialProof
          ctaText="Read more reviews"
          heading="Hear from our users"
          href="https://www.g2.com/products/wistia/reviews"
          showAllBadges={false}
          showPodium={false}
        >
          <TestimonialCard
            attribution="Marko Novakovic"
            attributionTitle="Head of Creative at FishingBooker"
            backgroundColor="blue200"
            headline="Hooked on Wistia for all the right reasons"
            quote="The dependable player, customizable interface, and data-rich analytics provided by Wistia go beyond conventional metrics, offering valuable insights into viewer engagement."
            showCite={false}
            ratingColor="blue"
            textColor="blue800"
          />
          <TestimonialCard
            attribution="Scarlett Bush"
            attributionTitle="Director of Operations at Explainly"
            backgroundColor="blue200"
            headline="Video management made easy!"
            quote="Thanks to Wistia, anyone on the Explainly team can easily upload, organize, and showcase our entire portfolio of videos to prospective clients in a sleek and searchable video gallery. For both our internal team and external clients, Wistia's intuitive interface ensures effortless navigation and quick access to all our content."
            showCite={false}
            ratingColor="blue"
            textColor="blue800"
          />
          <TestimonialCard
            attribution="Jake Matulewicz"
            attributionTitle="Director of Marketing & Customer Experience at Symmons Industries"
            backgroundColor="blue200"
            headline="The best video marketing platform"
            quote="Wistia is simple to use, highly customizable, and lightning fast. The analytics capabilities within the tool are really well built. I have used a couple of competitors in the past that weren’t nearly as streamlined and easy to use as Wistia!"
            showCite={false}
            ratingColor="blue"
            textColor="blue800"
          />
          <TestimonialCard
            attribution="M. Tre Gonzalez"
            attributionTitle="Director of Growth at OnCourse Systems for Education"
            backgroundColor="blue200"
            headline="Elevated video marketing strategy made simple"
            quote="Wistia has found the perfect balance for a video hosting platform: It’s simple to use with the analytics we need to make our videos more effective. We now use it for everything: lead capture for marketing videos, chaptered long-form webinars, internal documentation—the works. It’s my favorite platform in our marketing stack."
            showCite={false}
            ratingColor="blue"
            textColor="blue800"
          />
        </G2SocialProof>

        <FeatureList
          ctaText="See all our features"
          description="We’ve got all the tools you need to squeeze more juice out of your videos."
          features={[
            'record',
            'marketing_automation',
            'video_editing',
            'podcasting',
            'collaboration',
            'video_seo',
            'lead_generation',
            'platform',
            'live_streaming',
          ]}
          headline="Video hosting is just the beginning"
          href="/product"
          imageAlt="Photo of a smiling man with a laptop"
          imageHeight={583}
          imageSrc="https://images.ctfassets.net/buksmp4dkugj/7AJqbpGJl1mXvZRSQvJdk9/3f72defd854be5724b7796fb4993cfff/hosting-feature-image.jpg"
          imageWidth={528}
        />
        <FAQ
          description="Get all the As to your FAQs."
          headline="Frequently asked questions"
        >
          <Accordion
            description="That depends on what you’re looking for. Wistia is great for converting viewers to customers and controlling how your videos look and feel, while YouTube helps you maximize your videos’ reach. We recommend using both as part of your video marketing strategy. In fact, you can publish videos to YouTube right from Wistia."
            headline="What is the best video hosting platform for me? Wistia or YouTube?"
          />
          <Accordion
            description="You can host up to 10 videos for free right here on Wistia. We’ll provide a customizable player, integrations with your social media accounts for quick posting, and video analytics to help you measure performance."
            headline="Where can I host videos for free?"
          />
          <Accordion
            description="Video hosting is when you upload video files to a platform so that people can watch them online. With Wistia, you can do just that. When you upload videos to Wistia, you can customize the video player to match your brand, easily embed the videos on your website, and use the privacy settings to control who’s able to watch. You can even optimize your videos’ performance with access to analytics, video A/B testing, and integrations with your marketing automation platform."
            headline="What is video hosting?"
          />
          <Accordion
            description={
              <>
                <p>Get a quick overview of our free and paid plans:</p>

                <ul>
                  <li>
                    Wistia’s Free Plan lets you host up to 10 videos, customize
                    the video player to match your brand, integrate with your
                    social media accounts for quick posting, and measure the
                    impact of your videos with basic video analytics.{' '}
                  </li>
                  <li>
                    Wistia’s Plus Plan ($19/mo) helps you capture leads with
                    in-video forms, integrate your video library with your email
                    marketing platform, and do basic video editing to get more
                    out of your videos.
                  </li>
                  <li>
                    Wistia’s Pro Plan ($79/mo) offers video heatmaps, the
                    ability to A/B test with video, and customizable in-video
                    calls to action (CTAs) that drive your audience where you
                    want them to go.
                  </li>
                  <li>
                    Wistia’s Advanced Plan ($319/mo) helps you host super
                    engaging webinars and live events. Plus, it lets you
                    integrate Wistia with your marketing automation platform
                    (HubSpot, Marketo Engage, or Pardot). You can also create an
                    unlimited number of Wistia Channels, which are customizable
                    video galleries that look ultra polished and match your
                    brand to a tee.
                  </li>
                </ul>
              </>
            }
            headline="What’s the difference between paid and free video hosting with Wistia?"
          />
          <Accordion
            description="Just create an account with us! Then upload your video with a quick drag and drop. Another option is to import a video from tools like Zoom. Haven’t made a video yet? No prob. With Wistia, you can create videos with our handy-dandy video recorder."
            headline="How do I host a video online?"
          />
          <Accordion
            description="It’s a whole lot easier to use a video hosting site like Wistia. Hosting your own videos requires a ton of server bandwidth. You’ll also have to deal with file size limits, storage space, slow load times, not-so-great video quality, possible piracy, manual coding, and video conversion for mobile—phew! What a mouthful. But you’re in luck because Wistia handles all of that for you."
            headline="Is it better to host my own videos or use a video hosting site?"
          />
          <Accordion
            description="We think of it as a website that lets you upload, host, and watch videos. With Wistia, you can do that and more. We’ll help you record and edit your own videos, host a webinar, organize your video library, build branded video galleries for all your content, optimize your videos for search, customize your video player, and measure the impact of your videos with top-notch analytics."
            headline="What is an online video platform?"
          />
        </FAQ>
        <Cta
          headline="Ready to take Wistia for a spin?"
          primaryCtaHref={WISTIA_FREE_ACCOUNT}
          primaryCtaText="Sign up for free"
          secondaryCtaHref="https://wistia.com/pricing/getintouch"
          secondaryCtaText="Get in touch"
        />
      </Section>
    </>
  );
};

export default Page;
